import { useEffect } from 'react';

interface FAQ {
  q: string;
  a: string;
}

interface FAQSectionProps {
  faqs: FAQ[];
  heading?: string;
  subheading?: string;
  pageUrl?: string;
}

const FAQSection = ({ faqs, heading = 'Frequently Asked Questions', subheading, pageUrl }: FAQSectionProps) => {
  useEffect(() => {
    if (!pageUrl) return;
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a,
        },
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-faq-schema', 'true');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.querySelectorAll('script[data-faq-schema]').forEach(el => el.remove());
    };
  }, [faqs, pageUrl]);

  return (
    <section className="py-16 sm:py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
          <h2 className="font-display font-bold text-2xl sm:text-4xl text-foreground mb-3">
            {heading}
          </h2>
          {subheading && (
            <p className="text-muted-foreground text-base sm:text-lg">{subheading}</p>
          )}
        </div>
        <div className="max-w-4xl mx-auto space-y-6">
          {faqs.map((faq, i) => (
            <article key={i} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
              <h3 className="font-display font-semibold text-foreground text-base sm:text-lg mb-3">
                {faq.q}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                {faq.a}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
